import { Box, Button, Grid, IconButton } from "@mui/material"
import { Ref, Suspense, forwardRef, lazy, useImperativeHandle, useState } from "react"
import { CustomPaper } from "../../../../../../../components/CustomPaper"
import { DynamicFormRef } from "../../../../../../../components/dynamicFormRef"
import { DynamicFormRefProps } from "../../../../../../../components/dynamicFormRef/types"
import Loader from "../../../../../../../components/loader"
import { icons } from "../../../../../../../utils/icons"
import { OptionActions } from "../../../../../components/optionActions"
import { useSectionContext } from "../contextProvider"
import { typeConfigs } from "../contextProvider/provider/constants"
import { SectionRef } from "../contextProvider/provider/types"
import { Question } from "../question"
import { SectionHeader } from "../sectionHeader"
import { useStyles } from "./styles"
import { SectionFormProps } from "./types"

const QuestionList = lazy(() => import('../question/questionList').then(({ QuestionList }) => ({ default: QuestionList })))
const SectionForm = forwardRef(({ id, removeSection, updateSectionData, sectionDefault, questionListDefault, displayRemoveButton, provided, sectionAmount, totalPoints, questionAmount, isSectionConfig }: SectionFormProps, ref: Ref<SectionRef>) => {
    const styles = useStyles()
    const sectionProvider = useSectionContext()
    const [addDescription, setAddDescription] = useState(false)
    const [edit, setEdit] = useState(!Boolean(sectionDefault?.sectionName))
    const [expand, setExpand] = useState(!Boolean(sectionDefault?.sectionName))

    const model: DynamicFormRefProps['model'] = {
        sectionName: {
            id: 'sectionName',
            label: 'Nombre de la sección',
            name: 'sectionName',
            type: "text",
            validations: { required: { value: true } },
            wrapper: ({ children, id: internalId }) => {
                return (
                    <Grid
                        key={internalId}
                        item
                        xs={Boolean(isSectionConfig) ? 9 : 12}
                        children={Boolean(isSectionConfig) ? children : (
                            <Box sx={styles.addSectionButton}>
                                {children}

                                {Boolean(displayRemoveButton) && <IconButton onClick={() => { setEdit(false) }} sx={styles.add}>{icons('simple-close')}</IconButton>}

                                <IconButton onClick={saveSection} sx={styles.add} className="blue">{icons('add')}</IconButton>
                            </Box>
                        )}
                    />
                )
            }
        },
        ...Boolean(isSectionConfig) && {
            sectionPoints: {
                id: 'sectionPoints',
                label: 'Puntuación de la sección',
                name: 'sectionPoints',
                type: 'text',
                validations: {
                    required: { value: true },
                    min: { value: 1 },
                    max: { value: 100 },
                    decimal: { value: true }
                },
                wrapper: ({ children, id: internalId }) => {
                    return (
                        <Grid
                            key={internalId}
                            item
                            xs={3}
                            children={(
                                <Box sx={styles.addSectionButton}>
                                    {children}

                                    {Boolean(displayRemoveButton) && <IconButton onClick={() => { setEdit(false) }} sx={styles.add}>{icons('simple-close')}</IconButton>}

                                    <IconButton onClick={saveSection} sx={styles.add} className="blue">{icons('add')}</IconButton>
                                </Box>
                            )}
                        />
                    )
                }
            }
        },
        ...!!addDescription && {
            description: {
                id: 'description',
                label: 'Descripción de la sección',
                name: 'description',
                type: "text",
                rows: 4,
                maxRows: 5,
                multiline: true,
                wrapper: ({ children, id }) => {
                    return (
                        <Grid
                            key={id}
                            item
                            xs={12}
                            children={(
                                <Box>
                                    {children}

                                    <Button onClick={() => { displayedDescription(false) }} variant="text">Ocultar descripción</Button>
                                </Box>
                            )}
                        />
                    )
                }
            }
        },
    }
    const displayedDescription = (value: boolean) => {
        setAddDescription(value)
    }

    useImperativeHandle(ref, () => ({
        getData: () => {
            return {
                questionList: questionListDefault || [],
                sectionData: sectionDefault
            }
        },
        getTotalSectionPoints: () => {
            if (sectionProvider.questionId) {
                return Number(sectionProvider.totalSectionPoints) + Number(sectionProvider.questionValuePoints)
            }
            return sectionProvider.totalSectionPoints
        },
        cancelEdit: sectionProvider.cancelEdit
    }))
    const saveSection = () => {
        const sectionRef = sectionProvider.refSections.refsByKey?.['section']
        if (sectionRef?.validate) {
            if (!sectionRef?.validate()) {
                if (sectionRef?.getValues) {
                    const data = sectionRef.getValues()
                    if (!isSectionConfig || (Number(data?.sectionPoints) >= sectionProvider.totalSectionPoints && Number(data?.sectionPoints) <= 100)) {
                        updateSectionData({ data: data ?? {}, id, index: 1, })
                        // sectionProvider.setSectionData(prev => ({ ...prev ?? {}, ...data }))
                        setEdit(false)
                    } else {
                        if (Number(data?.sectionPoints) <= 100) {
                            sectionProvider.setModal(prev => ({
                                ...prev,
                                open: true,
                                title: 'Advertencia',
                                description: 'Los puntos totales de la sección no pueden ser menor a la suma de los puntos correctos'
                            }))
                        }
                    }
                }
            }
        }
    }

    const actionPlanSection = () => {
        sectionProvider.openConfig({ id: null, name: typeConfigs.section })
    }

    const editSection = () => {
        sectionProvider.keepSafeQuestionData()
        setEdit(true)
    }
    const expandSection = () => {
        setExpand(!expand)
        sectionProvider.cancelEdit()
    }
    if (!edit) return (
        <CustomPaper id={`section-${id}`} sx={{
            // position: 'relative'
        }} elevation={3}>
            <SectionHeader
                questionListDefault={questionListDefault}
                provided={provided}
                isSectionConfig={isSectionConfig}
                isExpanded={expand}
                displayRemoveButton={displayRemoveButton}
                title={(sectionDefault?.sectionName ?? '') as string}
                actionPlan={actionPlanSection}
                sectionTotalPoints={String(sectionDefault?.sectionPoints ?? 0)}
                edit={editSection}
                expand={expandSection}
                remove={() => { removeSection({ id }) }}
            />
            {
                !!expand && (
                    <Question />
                )
            }

            {
                !!expand && (
                    <Suspense fallback={<Loader />}>
                        <QuestionList
                            questionList={questionListDefault || []}
                            questionId={sectionProvider.questionId}
                            arrow={sectionProvider.arrow}
                        />
                    </Suspense>
                )
            }
            {
                Boolean(sectionProvider.openOptionAction) && (
                    <OptionActions
                        averagePoints={Number(totalPoints)}
                        ref={sectionProvider.optionRef}
                        detailConfig={sectionProvider.detailConfig}
                        noHeader
                        active={sectionProvider.checkQuestionOrAnswerHaveEvidence}
                        questionAmount={questionAmount}
                        sectionAmount={sectionAmount}
                        header={sectionProvider.headerConfig}
                        display={{
                            ...sectionProvider.cannotSeeConfig,
                            actionPlan: sectionProvider.isGeneral ? false : sectionProvider.cannotSeeConfig.actionPlan,
                            autoActionPlan: sectionProvider.isGeneral ? false : sectionProvider.cannotSeeConfig.autoActionPlan,
                            reincidence: sectionProvider.cannotSeeConfig.reincidence,
                        }}
                        idKey={sectionProvider.idToOptionAction}
                        key={sectionProvider.idToOptionAction}
                        save={sectionProvider.saveConfigurations}
                        defaults={sectionProvider.optionActionDefaultValues}
                        drawerProps={{ open: Boolean(sectionProvider.openOptionAction), anchor: 'right' }}
                    />
                )
            }
        </CustomPaper>
    )

    return (
        <Box sx={styles.container}>
            <DynamicFormRef
                model={model}
                ref={(element) => sectionProvider.refSections.addRef({ element, name: 'section' })}
                defaultValues={sectionDefault}
                parentWrapper={({ children }) => <Grid container spacing={3} children={children} />}
            />

            {
                !addDescription && (
                    <Button onClick={() => { displayedDescription(true) }} variant="text">Agregar descripción</Button>
                )
            }
        </Box>
    )
})

export { SectionForm }
